import { lazy } from "react";
import {
  FiActivity,
  FiAlertTriangle,
  FiAward,
  FiBarChart2,
  FiBook,
  FiBookOpen,
  FiLayers,
  FiBriefcase,
  FiCalendar,
  FiCpu,
  FiGrid,
  FiHelpCircle,
  FiHome,
  FiInbox,
  FiLayout,
  FiLifeBuoy,
  FiLink,
  FiMessageSquare,
  FiPlayCircle,
  FiSliders,
  FiSmile,
  FiStar,
  FiUserCheck,
  FiUsers,
  FiWifi,
} from "react-icons/fi";

const Login = lazy(() => import("../pages/Login"));
const ForgetPassword = lazy(() => import("../pages/ForgetPassword"));
const Dashboard = lazy(() => import("../pages/Dashboard"));
const Analytics = lazy(() => import("../pages/Analytics"));
const ApiUsage = lazy(() => import("../pages/ApiUsage"));
const LiveUsers = lazy(() => import("../pages/LiveUsers"));
const ActivityLogs = lazy(() => import("../pages/ActivityLogs"));
const StudentsManagement = lazy(() => import("../pages/StudentsManagement"));
const EducatorManagement = lazy(() => import("../pages/EducatorManagement"));
const SocialWorkerManagement = lazy(() => import("../pages/SocialWorker"));
const DistressAlerts = lazy(() => import("../pages/DistressAlerts"));
const ResourceHub = lazy(() => import("../pages/ResourceHub"));
const Resources = lazy(() => import("../pages/Resources"));
const ResourceSection = lazy(() => import("../pages/ResourceSection"));
const ResourceEditor = lazy(() => import("../pages/ResourceEditor"));
const Questions = lazy(() => import("../pages/Questions"));
const AddQuestions = lazy(() => import("../pages/AddQuestions"));
const ViewQuestions = lazy(() => import("../pages/ViewQuestions"));
const EditQuestions = lazy(() => import("../pages/EditQuestions"));
const GameResources = lazy(() => import("../pages/GameResources"));
const AddLevel = lazy(() => import("../pages/AddLevel"));
const EditLevel = lazy(() => import("../pages/EditLevel"));
const ViewLevel = lazy(() => import("../pages/ViewLevel"));
const KowalaScoreboard = lazy(() => import("../pages/KowalaScoreboard"));
const Cms = lazy(() => import("../pages/Cms"));
const Classes = lazy(() => import("../pages/Crud/Classes"));
const Schools = lazy(() => import("../pages/Crud/School"));
const Moods = lazy(() => import("../pages/Crud/Mood"));
const Prompts = lazy(() => import("../pages/Crud/Prompt"));
const Activity = lazy(() => import("../pages/Crud/Activity"));
const Partners = lazy(() => import("../pages/Crud/Partners"));
const Testimonials = lazy(() => import("../pages/Crud/Testimonials"));
const Feedback = lazy(() => import("../pages/Feedback"));
const HelpAndSupport = lazy(() => import("../pages/HelpAndSupport"));
const Demo = lazy(() => import("../pages/Demo"));
const Configuration = lazy(() => import("../pages/Configuration"));
const Notifications = lazy(() => import("../pages/Notifications"));
const ProfileSetting = lazy(() => import("../pages/ProfileSettings"));

// Sidebar sections, rendered in this order; empty ones are skipped.
export const SIDEBAR_GROUPS = {
  overview: "Overview",
  users: "Users",
  care: "Care",
  content: "Content",
  catalog: "Catalog",
  monitoring: "Monitoring",
  inbox: "Inbox",
  system: "System",
};

export const sideBarGroupOrder = Object.values(SIDEBAR_GROUPS);

export const PROFILE_SETTINGS_PATH = "/profile-setting";

/**
 * Every route in the app. Sidebar entries have `showInSidebar`, a
 * `group` and an `icon`; `title` is also the top-bar page title.
 * Detail routes (add/edit/view) resolve their title from `title` too.
 */
export const routes = [
  // Auth
  {
    path: "/login",
    protected: false,
    title: "Login",
    element: <Login />,
  },
  {
    path: "/forget-password",
    protected: false,
    title: "Forgot Password",
    element: <ForgetPassword />,
  },

  // Overview
  {
    path: "/",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.overview,
    title: "Dashboard",
    element: <Dashboard />,
    icon: <FiGrid />,
  },
  {
    path: "/analytics",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.overview,
    title: "Analytics",
    element: <Analytics />,
    icon: <FiBarChart2 />,
  },
  {
    path: "/api-usage",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.overview,
    title: "API Usage",
    element: <ApiUsage />,
    icon: <FiCpu />,
  },

  // Monitoring
  {
    path: "/live-users",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.monitoring,
    title: "Live Users",
    element: <LiveUsers />,
    icon: <FiWifi />,
  },
  {
    path: "/activity-logs",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.monitoring,
    title: "Activity Logs",
    element: <ActivityLogs />,
    icon: <FiActivity />,
  },

  // Users
  {
    path: "/students-management",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.users,
    title: "Students",
    element: <StudentsManagement />,
    icon: <FiUsers />,
  },
  {
    path: "/educators-management",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.users,
    title: "Educators",
    element: <EducatorManagement />,
    icon: <FiUserCheck />,
  },
  {
    path: "/social-workers-management",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.users,
    title: "Social Workers",
    element: <SocialWorkerManagement />,
    icon: <FiBriefcase />,
  },

  // Care
  {
    path: "/distress-alerts",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.care,
    title: "Distress Alerts",
    element: <DistressAlerts />,
    icon: <FiAlertTriangle />,
  },

  // Content
  {
    path: "/resource-hub",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.content,
    title: "Videos",
    element: <ResourceHub />,
    icon: <FiPlayCircle />,
  },
  {
    path: "/resources",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.content,
    title: "Resources",
    element: <Resources />,
    icon: <FiLayers />,
  },
  {
    path: "/resources/:sectionId",
    protected: true,
    title: "Resources",
    element: <ResourceSection />,
  },
  {
    path: "/resources/:sectionId/new",
    protected: true,
    title: "Add Resource",
    element: <ResourceEditor />,
  },
  {
    path: "/resources/item/:itemId",
    protected: true,
    title: "Edit Resource",
    element: <ResourceEditor />,
  },
  {
    path: "/questions",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.content,
    title: "Questions",
    element: <Questions />,
    icon: <FiHelpCircle />,
  },
  {
    path: "/questions/add",
    protected: true,
    title: "Add Question",
    element: <AddQuestions />,
  },
  {
    path: "/questions/view/:id",
    protected: true,
    title: "View Question",
    element: <ViewQuestions />,
  },
  {
    path: "/questions/edit/:id",
    protected: true,
    title: "Edit Question",
    element: <EditQuestions />,
  },
  {
    path: "/game-resources",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.content,
    title: "Game Resources",
    element: <GameResources />,
    icon: <FiLayout />,
  },
  {
    path: "/game-resources/add",
    protected: true,
    title: "Add Level",
    element: <AddLevel />,
  },
  {
    path: "/game-resources/edit/:id",
    protected: true,
    title: "Edit Level",
    element: <EditLevel />,
  },
  {
    path: "/game-resources/view/:id",
    protected: true,
    title: "View Level",
    element: <ViewLevel />,
  },
  {
    path: "/kowala-defender-scoreboard",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.content,
    title: "Kowala Scoreboard",
    element: <KowalaScoreboard />,
    icon: <FiAward />,
  },
  {
    path: "/cms",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.content,
    title: "CMS",
    element: <Cms />,
    icon: <FiBookOpen />,
  },

  // Catalog
  {
    path: "/classes",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.catalog,
    title: "Classes",
    element: <Classes />,
    icon: <FiBook />,
  },
  {
    path: "/schools",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.catalog,
    title: "Schools",
    element: <Schools />,
    icon: <FiHome />,
  },
  {
    path: "/moods",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.catalog,
    title: "Moods",
    element: <Moods />,
    icon: <FiSmile />,
  },
  {
    path: "/prompts",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.catalog,
    title: "Prompts",
    element: <Prompts />,
    icon: <FiMessageSquare />,
  },
  {
    path: "/activities",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.catalog,
    title: "Activities",
    element: <Activity />,
    icon: <FiActivity />,
  },
  {
    path: "/partners",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.catalog,
    title: "Partners",
    element: <Partners />,
    icon: <FiLink />,
  },
  {
    path: "/testimonials",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.catalog,
    title: "Testimonials",
    element: <Testimonials />,
    icon: <FiStar />,
  },

  // Inbox
  {
    path: "/feedback",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.inbox,
    title: "Feedback",
    element: <Feedback />,
    icon: <FiInbox />,
  },
  {
    path: "/help-and-support",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.inbox,
    title: "Help & Support",
    element: <HelpAndSupport />,
    icon: <FiLifeBuoy />,
  },
  {
    path: "/demo-schedule",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.inbox,
    title: "Demo Schedule",
    element: <Demo />,
    icon: <FiCalendar />,
  },

  // System
  {
    path: "/configuration",
    protected: true,
    showInSidebar: true,
    group: SIDEBAR_GROUPS.system,
    title: "Configuration",
    element: <Configuration />,
    icon: <FiSliders />,
  },

  // Reached from the top bar / sidebar footer
  {
    path: "/notifications",
    protected: true,
    title: "Notifications",
    element: <Notifications />,
  },
  {
    path: PROFILE_SETTINGS_PATH,
    protected: true,
    title: "Profile Settings",
    element: <ProfileSetting />,
  },
];
